import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import DashboardLayoutEditor from '../DashboardLayoutEditor';
import Account from 'Screens/Contextualization/Settings/components/Account';
import { EEFeatureSlot } from 'Common/ee/EEFeatureSlot';
import { getEEFeature } from 'Common/ee/registry';
import Saml from 'Screens/Contextualization/Settings/components/Saml';
import Ldap from 'Screens/Contextualization/Settings/components/Ldap';
import { ReactComponent as AccountIcon } from 'Images/preferences_icons_Account.svg';
import { ReactComponent as TicketingIcon } from 'Images/preferences_icons_Ticketing.svg';
import { ReactComponent as AuthIcon } from 'Images/preferences_icons_Authentication.svg';
import { ReactComponent as IntegrationsIcon } from 'Images/customAttributes.svg';
import { ReactComponent as CommandIcon } from 'Images/icon-customfields.svg';
import {
  PERMISSION_TYPES,
  FEATURE_SETTINGS_REPORTING,
  FEATURE_USER_TOKENS
} from 'Hooks/usePermission/permissions';
import { selectAllowedByFeature, selectAllowedByRole } from 'Hooks/usePermission/selector';
import {
  Wrapper,
  Tabs,
  TabGroup,
  Title,
  Header,
  IconWrapper,
  Subtitle,
  Item,
  TabContent,
  Content
} from './styled';
import 'Screens/Contextualization/Settings/styles.scss';
import CustomAttributes from '../CustomAttributes';
import { selectIsAdmin } from 'store/Faraday/selectors';
import AccessTokens from '../AccessTokens';
import TicketingTools from '../TicketingTools';

const Menu = ({ onClose, defaultTab = 'account' }) => {
  const defaultVisibleComponent = defaultTab === 'notifications' && getEEFeature('settings-notifications-tab')
    ? (
      <EEFeatureSlot
        featureKey="settings-notifications-tab"
        render={ (NotificationsPreferences) => <NotificationsPreferences onClose={ onClose } /> }
      />
      )
    : <Account onClose={ onClose } />;
  const intl = useIntl();
  const [visibleComponent, setVisibleComponent] = useState(defaultVisibleComponent);
  const [active, setActive] = useState(defaultTab);
  const isAccountEnabled = true;

  const isAdmin = useSelector(selectIsAdmin);

  const isReportsEnabled = useSelector((state) => selectAllowedByRole(state, FEATURE_SETTINGS_REPORTING, PERMISSION_TYPES.PATCH));
  const isCustomAttributesEnabled = true;
  const isUserTokensEnabled = useSelector((state) => selectAllowedByRole(state, FEATURE_USER_TOKENS, PERMISSION_TYPES.READ) && selectAllowedByFeature(state, FEATURE_USER_TOKENS, PERMISSION_TYPES.READ));

  const click = (key, component) => { setActive(key); setVisibleComponent(component); };

  const menuItems = [
    {
      canView: isAccountEnabled || isReportsEnabled,
      items: [
        {
          key: 'account', title: intl.formatMessage({ id: 'preferences.account.title' }), click: () => { click('account', <Account onClose={ onClose } />); }, icon: <AccountIcon />, canView: true
        }
      ]
    },
    {
      title: intl.formatMessage({ id: 'preferences.instanceSettings.title' }),
      canView: isAccountEnabled || isReportsEnabled,
      icon: <CommandIcon />,
      items: [
        {
          key: 'report',
          title: 'Report',
          click: () => {
            click('report', (
              <EEFeatureSlot
                featureKey="settings-report-tab"
                render={ (Report) => <Report onClose={ onClose } /> }
              />
            ));
          },
          canView: Boolean(getEEFeature('settings-report-tab'))
        },
        {
          key: 'customAttributes', title: intl.formatMessage({ id: 'preferences.customAttributes.title' }), click: () => { click('customAttributes', <CustomAttributes onClose={ onClose } />); }, canView: isCustomAttributesEnabled
        },
        {
          key: 'editDashboard',
          title: intl.formatMessage({ id: 'preferences.dashboard.key' }),
          canView: true,
          click: () => click('editDashboard', <DashboardLayoutEditor onClose={ onClose } />)
        }
      ]
    },
    {
      title: intl.formatMessage({ id: 'preferences.authentication.title' }),
      canView: true,
      icon: <AuthIcon />,
      items: [
        {
          key: 'ldap', title: intl.formatMessage({ id: 'preferences.ldap.title' }), click: () => { click('ldap', <Ldap onClose={ onClose } />); }, canView: true
        },
        {
          key: 'saml', title: intl.formatMessage({ id: 'preferences.saml.title' }), click: () => { click('saml', <Saml onClose={ onClose } />); }, canView: true
        },
        {
          key: 'accessTokens', title: intl.formatMessage({ id: 'preferences.accessTokens.title' }), click: () => { click('accessTokens', <AccessTokens onClose={ onClose } />); }, canView: isUserTokensEnabled
        }
      ]
    },
    {
      title: intl.formatMessage({ id: 'preferences.ticketingTools.title' }),
      canView: isAdmin,
      icon: <TicketingIcon />,
      items: [
        {
          key: 'jira', title: intl.formatMessage({ id: 'preferences.jira.title' }), click: () => { click('jira', <TicketingTools onClose={ onClose } />); }, canView: isAdmin
        },
        {
          key: 'serviceNow', title: intl.formatMessage({ id: 'preferences.serviceNow.title' }), click: () => { click('serviceNow', <TicketingTools onClose={ onClose } />); }, canView: isAdmin
        },
        {
          key: 'webHelpDesk', title: intl.formatMessage({ id: 'preferences.webHelpDesk.title' }), click: () => { click('webHelpDesk', <TicketingTools onClose={ onClose } />); }, canView: isAdmin
        },
        {
          key: 'gitlab', title: intl.formatMessage({ id: 'preferences.gitlab.title' }), click: () => { click('gitlab', <TicketingTools onClose={ onClose } />); }, canView: isAdmin
        },
        {
          key: 'serviceDesk', title: intl.formatMessage({ id: 'preferences.serviceDesk.title' }), click: () => { click('serviceDesk', <TicketingTools onClose={ onClose } />); }, canView: isAdmin
        }
      ]
    },
    {
      title: intl.formatMessage({ id: 'preferences.integrations.title' }),
      canView: Boolean(getEEFeature('settings-notifications-tab')),
      icon: <IntegrationsIcon />,
      items: [
        {
          key: 'notifications',
          title: intl.formatMessage({ id: 'preferences.notifications.title' }),
          click: () => {
            click('notifications', (
              <EEFeatureSlot
                featureKey="settings-notifications-tab"
                render={ (NotificationsPreferences) => <NotificationsPreferences onClose={ onClose } /> }
              />
            ));
          },
          canView: Boolean(getEEFeature('settings-notifications-tab'))
        }
      ]
    }
  ];

  return (
    <Wrapper>
      <Tabs>
        <Title>{intl.formatMessage({ id: 'preferences.title' })}</Title>
        <Content>
          { menuItems.filter((group) => group.canView).map((group) => (
            <TabGroup key={ group.title }>
              <Header>
                { group.icon ? <IconWrapper>{ group.icon }</IconWrapper> : null }
                <Subtitle hasIcon={ group.icon }>{group.title}</Subtitle>
              </Header>
              { group.items.filter((item) => item.canView).map((item) => (
                <Item key={ item.key } active={ active === item.key } name={ item.key } onClick={ item.click } hasIcon={ item.icon }>
                  { item.icon ? item.icon : null }
                  {' '}
                  {item.title}
                </Item>
              ))}
            </TabGroup>
          ))}
        </Content>
      </Tabs>
      <TabContent $noScroll={ active === 'editDashboard' }>
        {visibleComponent}
      </TabContent>
    </Wrapper>
  );
};

Menu.propTypes = {
  onClose: PropTypes.func.isRequired
};

export default Menu;
